/**
 * 分叉记录 — latest ORB fork/split event only (只显示最后一个拆分).
 * URL: /fork-records
 *
 * GET /api/users/fork-record/latest
 */
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { usersAPI } from '../api/users';
import { EmptyState } from '../components/ui/EmptyState';
import { LoadingSkeleton } from '../components/ui/LoadingSkeleton';
import { QueryErrorNote } from '../components/ui/QueryErrorNote';
import { ThemedPageHeader } from '../components/ui/ThemedPageHeader';
import { THEMED_SURFACE_COLORS as C } from '../components/ui/ThemedSurface';

const PANEL_BG = '#222d3f';
const PAGE_BG = '#131f2f';
const GRID_LINE = '#31405a';
const GOLD = '#e3c56b';

function fmtTime(value: string): string {
  const trimmed = (value ?? '').trim();
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(trimmed)) return trimmed;
  const date = new Date(trimmed.endsWith('Z') ? trimmed : `${trimmed.replace(' ', 'T')}Z`);
  if (Number.isNaN(date.getTime())) return trimmed;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function fmtNum(value: string, maxFrac = 4): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  return n.toLocaleString(undefined, { maximumFractionDigits: maxFrac });
}

function Cell({
  label,
  value,
  labelColor,
  valueColor,
}: {
  label: string;
  value: string;
  labelColor: string;
  valueColor: string;
}) {
  return (
    <div className="flex flex-1 flex-col items-center gap-3 px-2 py-4 text-center">
      <span className="text-sm" style={{ color: labelColor }}>
        {label}
      </span>
      <span className="text-sm font-medium tabular-nums" style={{ color: valueColor }}>
        {value}
      </span>
    </div>
  );
}

export function ForkRecordsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['users', 'fork-record', 'latest'],
    queryFn: () => usersAPI.getLatestForkRecord(),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });

  const record = data?.record ?? null;

  const headerTheme = {
    topbarBg: PAGE_BG,
    titleGradient: C.titleGradient,
    text: C.text,
  };

  return (
    <div className="min-h-full" style={{ background: PAGE_BG }}>
      <ThemedPageHeader
        title={t('me_finance.fork_records', '分叉记录')}
        onBack={() => navigate(-1)}
        theme={headerTheme}
      />

      <div className={`px-4 pb-8 pt-[68px]${isFetching && data ? ' opacity-80' : ''}`}>
        {isError && (
          <QueryErrorNote
            message={t('common.error', '加载失败')}
            onRetry={() => void refetch()}
          />
        )}

        {isLoading && !data ? (
          <LoadingSkeleton className="h-56" />
        ) : !record ? (
          <EmptyState title={t('common.noData', '暂无数据')} className="py-16" />
        ) : (
          <div
            className="overflow-hidden rounded-2xl border"
            style={{ borderColor: GRID_LINE, background: PANEL_BG }}
          >
            {/* Row group 1: 时间 / 批次 / 倍率 / 前币量 */}
            <div className="flex" style={{ borderBottom: `1px solid ${GRID_LINE}` }}>
              <Cell label={t('fork.time', '分叉时间')} value={fmtTime(record.create_time)} labelColor={C.text} valueColor={C.text} />
              <div style={{ width: 1, background: GRID_LINE }} />
              <Cell label={t('fork.batch', '分叉批次')} value={record.count || '—'} labelColor={C.text} valueColor={C.text} />
              <div style={{ width: 1, background: GRID_LINE }} />
              <Cell label={t('fork.multiple', '分叉倍率')} value={fmtNum(record.multiple, 2)} labelColor={C.text} valueColor={C.text} />
              <div style={{ width: 1, background: GRID_LINE }} />
              <Cell label={t('fork.beforeAmount', '分叉前币量')} value={fmtNum(record.before_uo, 2)} labelColor={GOLD} valueColor={C.text} />
            </div>

            {/* Row group 2: 后币量 / 前币价 / 后币价 */}
            <div className="flex">
              <Cell label={t('fork.afterAmount', '分叉后币量')} value={fmtNum(record.after_uo, 2)} labelColor={GOLD} valueColor={C.text} />
              <div style={{ width: 1, background: GRID_LINE }} />
              <Cell label={t('fork.beforePrice', '分叉前币价')} value={fmtNum(record.before_unit_price, 4)} labelColor={GOLD} valueColor={C.text} />
              <div style={{ width: 1, background: GRID_LINE }} />
              <Cell label={t('fork.afterPrice', '分叉后币价')} value={fmtNum(record.after_unit_price, 4)} labelColor={GOLD} valueColor={C.text} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ForkRecordsPage;
